import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/features/common/ui';
import { markInstallBannerSeen, shouldShowInstallBanner } from './install';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * A small bar, from the third visit, that says the app can live on the home
 * screen. Mounted inside the signed-in shell only: a guest has not decided
 * to use Fint yet, let alone to install it.
 *
 * ── WHEN IT SHOWS ──
 * `shouldShowInstallBanner` decides from what is stored (the visit count, the
 * last time it was shown, whether the app was installed); this adds the one
 * thing storage cannot know, which is whether there is anything to offer.
 * A prompt to open, or an iPhone/iPad where the two taps can be spelled out.
 * On a desktop browser with no prompt - Firefox, Safari - there is nothing
 * for the bar to do except point at a menu it cannot name, and a bar that
 * cannot help is not shown. Being shown counts as seen: it does not come
 * back for thirty days whether it was closed or ignored.
 *
 * Fixed above the phone tab bar and its raised Add button (5rem, plus the
 * home indicator through the layout's --safe-bottom) and in the corner from
 * `sm`, where the tab bar gives way to the sidebar, so it covers no control
 * on either.
 */
export function InstallBanner() {
  const { canPrompt, promptInstall, installed, standalone, platform } = useInstallPrompt();
  const isApple = platform === 'iphone' || platform === 'ipad';
  const [due] = useState(() => shouldShowInstallBanner(localStorage, Date.now(), standalone));
  const [closed, setClosed] = useState(false);

  const visible = due && !closed && !installed && (canPrompt || isApple);

  useEffect(() => {
    if (visible) markInstallBannerSeen(localStorage, Date.now());
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Install Fint"
      data-testid="install-banner"
      className="fixed inset-x-3 bottom-[calc(5rem+var(--safe-bottom))] z-20 flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-sm text-card-foreground shadow-lg print:hidden sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-sm"
    >
      <img src="/icons/icon-192.png" alt="" width={40} height={40} className="size-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">Put Fint on your home screen</p>
        <p className="text-xs text-muted-foreground">
          {canPrompt ? 'Its own window, its own icon, one tap away.' : (
            <>
              Tap <Share className="inline size-3.5 align-text-bottom text-primary" aria-label="Share" /> then
              &ldquo;Add to Home Screen&rdquo;.
            </>
          )}
        </p>
      </div>
      {canPrompt ? (
        <Button
          size="sm"
          onClick={() => {
            void promptInstall().catch(() => undefined);
          }}
          data-testid="install-banner-prompt"
        >
          <Download aria-hidden />
          Install
        </Button>
      ) : (
        <Button size="sm" variant="outline" asChild>
          <Link to="/settings" onClick={() => setClosed(true)}>How</Link>
        </Button>
      )}
      <button
        type="button"
        onClick={() => setClosed(true)}
        aria-label="Not now"
        className="-m-1 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
